import { getMediaFileType, MediaType } from "./../../wix-ricos-common";
import {
  AUDIO_TYPE,
  FILE_UPLOAD_TYPE,
  IMAGE_TYPE,
  MediaUploadErrorKey,
  POLL_TYPE,
  VIDEO_TYPE
} from "./../../ricos-content";
import { getUploadError } from "./mediaErrorHandler";
import { MediaManagerUploader } from "./mediaManagerUploader";
const getHighestQualityVideo = (videos) => {
  const mp4 = videos.filter((video) => video.format === "mp4");
  const getVideoQuality = (video) => parseInt(video.quality, 10);
  return mp4.reduce(
    (result, video) => getVideoQuality(video) > getVideoQuality(result) ? video : result
  );
};
const getFilePluginComponentData = (fileName, fileUrl, type, privacy = "public", size) => {
  const componentData = {
    name: fileName,
    type,
    id: fileUrl,
    privacy
  };
  return size ? { ...componentData, size } : componentData;
};
const getFolderId = (userId, siteOwnerId, visitorUploads) => {
  const isOwner = siteOwnerId === userId;
  const { folderId, shouldIncludeSiteOwner } = visitorUploads || {};
  const shouldUploadToFolder = shouldIncludeSiteOwner || !isOwner;
  return shouldUploadToFolder ? folderId : null;
};
function isFileSizeExcceeded(file, fileUploadSizeLimit) {
  return !!fileUploadSizeLimit && file.size >= fileUploadSizeLimit * 1024 * 1024;
}
let mediaManagerUploader;
function uploadStrategy(metaSiteId, userId, siteOwnerId, instance, hasPlugin, getConfig, getWixClientPromise, visitorUploads, consumer, fileUploadSizeLimit) {
  if (!(instance && metaSiteId)) {
    return {
      config: {}
    };
  }
  const folderId = getFolderId(userId, siteOwnerId, visitorUploads);
  if (!mediaManagerUploader) {
    mediaManagerUploader = new MediaManagerUploader(getWixClientPromise, consumer, folderId);
  }
  const handleUploadError = (error, updateEntity) => {
    const isOwner = siteOwnerId === userId;
    const mediaUploadError = getUploadError(error, metaSiteId, isOwner);
    updateEntity({ error: mediaUploadError });
  };
  const withFileSizeLimitation = (uploadFunction) => {
    return (file, updateEntity) => isFileSizeExcceeded(file, fileUploadSizeLimit) ? updateEntity({
      error: {
        key: MediaUploadErrorKey.SIZE_LIMIT,
        args: { maxLimit: fileUploadSizeLimit }
      }
    }) : uploadFunction(file, updateEntity);
  };
  const imageUpload = (file, updateEntity) => {
    return mediaManagerUploader.upload(file, MediaType.IMAGE).then(({ file_name, original_file_name, height, width }) => {
      updateEntity({
        data: {
          id: file_name,
          height,
          width,
          original_file_name,
          file_name
        }
      });
    }).catch((error) => {
      return handleUploadError(error, updateEntity);
    });
  };
  const videoUpload = (file, updateEntity) => {
    return mediaManagerUploader.upload(file, MediaType.VIDEO).then(({ file_output: { video, image } }) => {
      updateEntity({
        data: {
          pathname: getHighestQualityVideo(video).url,
          thumbnail: {
            pathname: image[0].url,
            width: image[0].width,
            height: image[0].height
          },
          title: file.name
        }
      });
    }).catch((error) => handleUploadError(error, updateEntity));
  };
  const audioUpload = (file, updateEntity) => {
    const isCoverImage = file.type.includes("image");
    const mediaType = isCoverImage ? MediaType.IMAGE : MediaType.AUDIO;
    return mediaManagerUploader.upload(file, mediaType).then((res) => {
      if (isCoverImage) {
        const { file_name, original_file_name, height, width } = res;
        return updateEntity({
          data: {
            id: file_name,
            height,
            width,
            original_file_name,
            file_name
          }
        });
      } else {
        const {
          file_output: { audio }
        } = res;
        updateEntity({
          data: {
            audio: { src: { id: audio[0].url } }
          }
        });
      }
    }).catch((error) => handleUploadError(error, updateEntity));
  };
  const getUnsupportedExtensionError = (fileName) => {
    return {
      key: MediaUploadErrorKey.UNSUPPORTED_EXTENSION,
      args: { extension: fileName.split(".").pop()?.toLowerCase() || "" }
    };
  };
  const galleryUpload = (file, updateEntity) => {
    const fileType = getMediaFileType(file.name);
    if (fileType === MediaType.IMAGE) {
      return imageUpload(file, updateEntity);
    } else if (fileType === MediaType.VIDEO) {
      return videoUpload(file, updateEntity);
    } else {
      return updateEntity({
        error: getUnsupportedExtensionError(file.name)
      });
    }
  };
  const fileUpload = (file, updateEntity) => {
    const fileType = getMediaFileType(file.name);
    const privacy = getConfig(FILE_UPLOAD_TYPE).privacy || "public";
    return mediaManagerUploader.upload(file, fileType, { privacy }).then(({ original_file_name: name, media_type, file_url, file_output }) => {
      const fileUrl = file_output?.video ? getHighestQualityVideo(file_output.video).url : file_url;
      const type = file.name.split(".").length > 1 ? file.name.split(".").pop() : media_type;
      const componentData = getFilePluginComponentData(name, fileUrl, type || "", privacy);
      updateEntity({ data: componentData });
    }).catch((error) => handleUploadError(error, updateEntity));
  };
  const uploadFunctions = {
    [IMAGE_TYPE]: withFileSizeLimitation(galleryUpload),
    [VIDEO_TYPE]: withFileSizeLimitation(videoUpload),
    [AUDIO_TYPE]: withFileSizeLimitation(audioUpload),
    [FILE_UPLOAD_TYPE]: withFileSizeLimitation(fileUpload)
  };
  return {
    helpers: {
      ...(hasPlugin(IMAGE_TYPE) || hasPlugin(POLL_TYPE)) && {
        handleFileUpload: uploadFunctions[IMAGE_TYPE]
      }
    },
    config: {
      ...hasPlugin(VIDEO_TYPE) && {
        [VIDEO_TYPE]: {
          handleFileUpload: uploadFunctions[VIDEO_TYPE],
          enableCustomUploadOnMobile: true
        }
      },
      ...hasPlugin(AUDIO_TYPE) && {
        [AUDIO_TYPE]: {
          handleFileUpload: uploadFunctions[AUDIO_TYPE]
        }
      },
      ...hasPlugin(FILE_UPLOAD_TYPE) && {
        [FILE_UPLOAD_TYPE]: {
          onFileSelected: uploadFunctions[FILE_UPLOAD_TYPE],
          accept: "*"
        }
      }
    }
  };
}
export {
  uploadStrategy as default,
  getFilePluginComponentData,
  getHighestQualityVideo
};
//# sourceMappingURL=uploadStrategy.js.map
